import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Zizo } from '../components/common/Zizo';
import { Page } from '../components/layout/Page';
import { LESSONS } from '../data/lessons';
import { LESSON_CONTENT } from '../data/lessonContent';
import { RESOURCES, getResourceTypeLabel } from '../data/resources';

const TOPIC_CONFIG = [
  {
    id: 'minerals',
    lessonId: 'lesson-1',
    category: 'minerals',
    keywords: ['معدن', 'معادن', 'ذهب', 'فوسفات', 'حديد', 'نحاس', 'تعدين', 'السكري', 'ابو طرطور'],
    intro: 'المعادن مهمة جدا في حياتنا، لأنها تدخل في البناء والصناعة والكهرباء.'
  },
  {
    id: 'energy',
    lessonId: 'lesson-2',
    category: 'energy',
    keywords: ['طاقة', 'شمسية', 'رياح', 'غاز', 'بترول', 'كهرباء', 'بنبان', 'ظهر'],
    intro: 'الطاقة تساعدنا في الإضاءة والتشغيل والنقل، وفي مصر عندنا طاقة تقليدية ومتجددة.'
  },
  {
    id: 'water',
    lessonId: 'lesson-3',
    category: null,
    keywords: ['مياه', 'ميه', 'النيل', 'نهر', 'ري', 'ترشيد', 'تحلية', 'تلوث', 'مطر', 'امطار'],
    intro: 'المياه أساس الحياة، ونهر النيل هو المصدر الأهم للمياه في مصر.'
  },
  {
    id: 'agriculture',
    lessonId: 'lesson-3',
    category: 'agriculture',
    keywords: ['زراعة', 'محصول', 'محاصيل', 'قمح', 'قطن', 'ارز', 'دلتا', 'فلاح'],
    intro: 'الزراعة مهمة لأنها توفر الغذاء، وتعتمد على المياه وطرق الري الجيدة.'
  },
  {
    id: 'tourism',
    lessonId: null,
    category: 'tourism',
    keywords: ['سياحة', 'اهرامات', 'اهرام', 'كرنك', 'معابد', 'اثار', 'آثار'],
    intro: 'السياحة في مصر ثروة كبيرة، وتعلمنا عن التاريخ والهوية المصرية.'
  }
];

const QA_LIST = [
  {
    q: 'ما هو الفوسفات؟',
    a: 'الفوسفات معدن مهم يدخل في صناعة الأسمدة الزراعية ويساعد على زيادة إنتاج المحاصيل.'
  },
  {
    q: 'لماذا الطاقة الشمسية مهمة؟',
    a: 'لأنها طاقة نظيفة ومتجددة وتقلل التلوث، ومصر لديها سطوع شمسي قوي معظم العام.'
  },
  {
    q: 'كيف نحافظ على المياه؟',
    a: 'بإغلاق الصنبور عند عدم الحاجة، وعدم تلويث النيل، واستخدام طرق ري حديثة.'
  },
  {
    q: 'ما أهمية نهر النيل؟',
    a: 'نهر النيل هو المصدر الرئيسي للمياه في مصر، ويعتمد عليه الناس في الشرب والزراعة والصناعة.'
  },
  {
    q: 'ما الفرق بين الطاقة المتجددة وغير المتجددة؟',
    a: 'الطاقة المتجددة مثل الشمس والرياح تتجدد طبيعيا، أما غير المتجددة مثل البترول والغاز فهي محدودة.'
  },
  {
    q: 'أين تقع محطة بنبان؟',
    a: 'محطة بنبان للطاقة الشمسية تقع في أسوان، وهي من أكبر مشروعات الطاقة الشمسية في إفريقيا.'
  },
  {
    q: 'ما أهمية منجم السكري؟',
    a: 'منجم السكري من أهم مناجم الذهب في مصر ويساعد في دعم الاقتصاد وتوفير فرص عمل.'
  },
  {
    q: 'ما فائدة الري بالتنقيط؟',
    a: 'الري بالتنقيط يوفر المياه ويوصل الماء مباشرة إلى الجذور، لذلك هو أنسب من طرق الري القديمة.'
  },
  {
    q: 'ما هي المياه الجوفية؟',
    a: 'المياه الجوفية هي مياه مخزنة تحت سطح الأرض وتعد مصدرا مهما خاصة في المناطق الصحراوية.'
  },
  {
    q: 'ما أهم المحاصيل الزراعية في مصر؟',
    a: 'من أهم المحاصيل: القمح والأرز والقطن والذرة، وكلها مرتبطة بموارد المياه وطرق الري.'
  },
  {
    q: 'لماذا نعيد تدوير المعادن؟',
    a: 'إعادة تدوير المعادن تقلل استهلاك الموارد الطبيعية، وتوفر طاقة، وتقلل التلوث.'
  },
  {
    q: 'أين يقع حقل ظهر؟',
    a: 'حقل ظهر يقع في البحر المتوسط قرب بورسعيد، وهو من أكبر حقول الغاز الطبيعي.'
  },
  {
    q: 'ما أهمية القطن المصري؟',
    a: 'القطن المصري مشهور عالميا بجودته العالية وطول أليافه، ويدخل في صناعة الملابس والمنسوجات.'
  },
  {
    q: 'كيف أوفر الكهرباء في البيت؟',
    a: 'أطفئ الأنوار غير الضرورية، وافصل الأجهزة عند عدم الاستخدام، واستخدم لمبات موفرة للطاقة.'
  },
  {
    q: 'ما أهمية أهرامات الجيزة؟',
    a: 'الأهرامات من أهم المعالم السياحية والأثرية في العالم، وتمثل جزءا مهما من تاريخ مصر.'
  },
  {
    q: 'اذكر مثالا لمورد زراعي على الخريطة.',
    a: 'من الأمثلة: زراعة الأرز في دلتا النيل، وحقول القطن في محافظة الدقهلية.'
  }
];

const SUGGESTED_QUESTIONS = [
  'ما أهمية نهر النيل؟',
  'كيف نحافظ على المياه؟',
  'ما هو الفوسفات؟',
  'ما أهمية منجم السكري؟',
  'اذكر مثالا لطاقة متجددة في مصر.',
  'لماذا الطاقة الشمسية مفيدة؟',
  'أين تقع محطة بنبان؟',
  'ما الفرق بين الطاقة المتجددة وغير المتجددة؟',
  'ما أهم المحاصيل في مصر؟',
  'لماذا الري بالتنقيط أفضل؟',
  'ما فائدة القطن المصري؟',
  'كيف تساعد المعادن في حياتنا؟'
];

const INITIAL_MESSAGE =
  'أهلا! أنا زيزو. اسألني سؤالا تعليميا عن موارد مصر (مياه، طاقة، معادن، زراعة)، وسأساعدك بإجابة مباشرة.';

const decodeArabicMojibake = (value = '') => {
  if (typeof value !== 'string' || !/[ØÙÃ]/.test(value)) return value;

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0)));
    const decoded = new TextDecoder('utf-8').decode(bytes);
    return /[\u0600-\u06ff]/.test(decoded) ? decoded : value;
  } catch {
    return value;
  }
};

const normalizeArabic = (text = '') =>
  decodeArabicMojibake(text)
    .toLowerCase()
    .replace(/[\u064b-\u065f]/g, '')
    .replace(/[إأآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[^\u0621-\u064aa-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokenMatchScore = (question, candidate) => {
  const q = normalizeArabic(question);
  const tokens = normalizeArabic(candidate)
    .split(' ')
    .filter((token) => token.length > 2);

  if (!tokens.length) return 0;
  const matches = tokens.filter((token) => q.includes(token)).length;
  return matches / tokens.length;
};

export const Chat = () => {
  const initialMessage = useMemo(
    () => ({
      role: 'assistant',
      content: INITIAL_MESSAGE
    }),
    []
  );

  const [messages, setMessages] = useState([initialMessage]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const listRef = useRef(null);

  const cleanLessons = useMemo(
    () =>
      LESSONS.map((lesson) => ({
        ...lesson,
        title: decodeArabicMojibake(lesson.title)
      })),
    []
  );

  const cleanFactsByLesson = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(LESSON_CONTENT).map(([lessonId, content]) => [
          lessonId,
          (content?.facts || []).map((fact) => decodeArabicMojibake(fact))
        ])
      ),
    []
  );

  const cleanResources = useMemo(
    () =>
      RESOURCES.map((resource) => ({
        ...resource,
        name: decodeArabicMojibake(resource.name),
        governorate: decodeArabicMojibake(resource.governorate),
        description: decodeArabicMojibake(resource.description),
        typeLabel: decodeArabicMojibake(getResourceTypeLabel(resource.type))
      })),
    []
  );

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages, isTyping]);

  const buildTopicAnswer = (topicId) => {
    const topic = TOPIC_CONFIG.find((item) => item.id === topicId);
    if (!topic) return null;

    const lesson = topic.lessonId ? cleanLessons.find((item) => item.id === topic.lessonId) : null;
    const topFacts = topic.lessonId ? (cleanFactsByLesson[topic.lessonId] || []).slice(0, 2) : [];
    const examples = topic.category
      ? cleanResources
          .filter((resource) => resource.category === topic.category)
          .sort((a, b) => (b.importance || 0) - (a.importance || 0))
          .slice(0, 2)
      : [];

    const parts = [topic.intro];

    if (topFacts.length > 0) {
      parts.push(`معلومة سريعة: ${topFacts[0]}`);
    }

    if (examples.length > 0) {
      const examplesText = examples.map((item) => `${item.name} (${item.governorate})`).join('، ');
      parts.push(`أمثلة من الخريطة: ${examplesText}.`);
    }

    if (lesson) {
      parts.push(`لو تحب، أشرح لك أكثر من درس: ${lesson.title}.`);
    }

    return parts.join(' ');
  };

  const findTopicByQuestion = (text) => {
    const normalized = normalizeArabic(text);
    let best = { id: null, score: 0 };

    TOPIC_CONFIG.forEach((topic) => {
      const score = topic.keywords.reduce((acc, keyword) => {
        const normKeyword = normalizeArabic(keyword);
        return normalized.includes(normKeyword) ? acc + 1 : acc;
      }, 0);

      if (score > best.score) {
        best = { id: topic.id, score };
      }
    });

    return best.score > 0 ? best.id : null;
  };

  const findResourceMatch = (text) => {
    const normalized = normalizeArabic(text);

    return cleanResources.find((resource) => {
      const name = normalizeArabic(resource.name);
      const governorate = normalizeArabic(resource.governorate);
      const typeLabel = normalizeArabic(resource.typeLabel);

      if (name && normalized.includes(name)) return true;
      if (governorate && normalized.includes(governorate)) return true;
      if (typeLabel && normalized.includes(typeLabel)) return true;

      const nameTokens = name.split(' ').filter((token) => token.length >= 4);
      return nameTokens.some((token) => normalized.includes(token));
    });
  };

  const buildAnswer = (question) => {
    const normalizedQuestion = normalizeArabic(question);

    if (!normalizedQuestion) {
      return 'اكتب سؤالك بشكل أوضح، وأنا أجاوبك فورا.';
    }

    const greetingKeywords = ['السلام', 'اهلا', 'مرحبا', 'ازيك', 'عامل ايه', 'انت مين', 'مين انت'];
    if (greetingKeywords.some((item) => normalizedQuestion.includes(normalizeArabic(item)))) {
      return 'أنا زيزو مساعدك التعليمي. اسألني عن النيل، الطاقة، المعادن، أو الزراعة في مصر.';
    }

    const bestDirect = QA_LIST.map((item) => ({ ...item, score: tokenMatchScore(question, item.q) })).sort(
      (a, b) => b.score - a.score
    )[0];

    if (bestDirect && bestDirect.score >= 0.55) {
      return `${bestDirect.a} لو تحب، أقدر أديك مثال من الخريطة كمان.`;
    }

    const resourceMatch = findResourceMatch(question);
    if (resourceMatch) {
      const stars = '⭐'.repeat(Math.max(1, resourceMatch.importance || 1));
      return `المكان ده موجود على الخريطة: ${resourceMatch.name} في ${resourceMatch.governorate}. نوع المورد: ${resourceMatch.typeLabel}. ${resourceMatch.description}. درجة الأهمية: ${stars}.`;
    }

    const topicId = findTopicByQuestion(question);
    if (topicId) {
      return buildTopicAnswer(topicId);
    }

    return 'سؤال ممتاز. جرّب تكتب كلمة مفتاحية أوضح مثل: النيل، الطاقة الشمسية، الفوسفات، الزراعة، وسأجاوبك بدقة.';
  };

  const handleSend = () => {
    if (!input.trim() || isTyping) return;

    const text = input.trim();
    const answer = buildAnswer(text);

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
      setIsTyping(false);
    }, 420);
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleSend();
  };

  return (
    <Page className="max-w-7xl mx-auto space-y-6">
      <section className="glass-panel p-6 md:p-10 relative overflow-hidden">
        <div className="absolute -top-28 -right-24 w-72 h-72 rounded-full bg-nileBlue/18 blur-3xl" />
        <div className="absolute -bottom-28 -left-24 w-80 h-80 rounded-full bg-palmGreen/14 blur-3xl" />

        <div className="relative flex items-center justify-between gap-6 flex-wrap">
          <div className="space-y-3">
            <h1 className="text-3xl md:text-5xl font-black text-gray-900">اسأل زيزو</h1>
            <p className="text-gray-700 max-w-2xl">
              مساعد تعليمي سريع: اسأل سؤالا واضحا، وسأرد عليك من محتوى الدروس والخريطة.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="chip chip-strong">تعليمي</span>
              <span className="chip">مياه</span>
              <span className="chip">طاقة</span>
              <span className="chip">معادن</span>
              <span className="chip">زراعة</span>
            </div>
          </div>

          <div className="shrink-0">
            <Zizo size="large" expression="happy" />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_350px] gap-5 items-start">
        <div className="glass-panel rounded-3xl p-4 md:p-5 flex flex-col min-h-[570px]">
          <div className="flex items-center justify-between gap-3 pb-2 border-b border-white/35">
            <div>
              <p className="font-black text-gray-900">المحادثة</p>
              <p className="text-xs text-gray-600">اسأل سؤال واحد واضح في كل رسالة</p>
            </div>
            <button
              type="button"
              className="chip"
              onClick={() => {
                if (isTyping) return;
                setMessages([initialMessage]);
              }}
            >
              مسح
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto mt-4 space-y-3 px-1 min-h-[360px]">
            {messages.map((msg, idx) => {
              const isUser = msg.role === 'user';
              return (
                <div key={`${msg.role}-${idx}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[92%] md:max-w-[85%] p-4 rounded-3xl text-sm leading-relaxed whitespace-pre-line ${
                      isUser ? 'text-white shadow-lg' : 'bg-white/70 border border-white/40 text-gray-900'
                    }`}
                    style={isUser ? { background: 'linear-gradient(135deg, #1E88E5 0%, #D4AF37 120%)' } : undefined}
                  >
                    {msg.content}
                  </div>
                </div>
              );
            })}

            {isTyping && (
              <div className="flex justify-start">
                <div className="max-w-[90%] p-4 rounded-3xl text-sm bg-white/80 border border-white/40 text-gray-700 animate-pulse">
                  زيزو يكتب الآن...
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="اكتب سؤالك هنا..."
              className="input-text"
              maxLength={220}
              disabled={isTyping}
            />
            <Button onClick={handleSend} disabled={isTyping || !input.trim()} className="sm:min-w-[112px]">
              إرسال
            </Button>
          </div>

          <div className="mt-3">
            <p className="text-xs text-gray-600 mb-2">أسئلة مقترحة سريعة</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {SUGGESTED_QUESTIONS.map((question) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => setInput(question)}
                  className="chip whitespace-nowrap hover:bg-white"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-24">
          <Card className="space-y-3">
            <h2 className="text-xl font-black text-gray-900">كيف تسأل زيزو؟</h2>
            <div className="space-y-2 text-sm text-gray-700">
              <p>1. ابدأ بـ: أين / كيف / لماذا / ما الفرق.</p>
              <p>2. أضف كلمة مفتاحية واضحة: النيل، بنبان، الفوسفات، الري.</p>
              <p>3. لو محتاج، اطلب مثال من الخريطة أو شرح أبسط.</p>
            </div>
          </Card>

          <Card className="space-y-3">
            <h2 className="text-xl font-black text-gray-900">أسئلة وأجوبة جاهزة</h2>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {QA_LIST.map((item) => (
                <details key={item.q} className="rounded-2xl border border-white/40 bg-white/60 p-3">
                  <summary className="cursor-pointer font-semibold text-gray-900">س: {item.q}</summary>
                  <p className="text-sm text-gray-700 mt-2">ج: {item.a}</p>
                </details>
              ))}
            </div>
          </Card>
        </aside>
      </section>
    </Page>
  );
};
